/**
 * IncidentDrillDown — expanded panel for an HSE card (DEV-822 / DEV-829).
 *
 * Renders inline below an HSE card when the user clicks it. Shows the
 * incident list for the card's category in the current period, with
 * severity pills, investigation status, and a "Show resolved" toggle.
 *
 * Behavior decisions baked in (per spec § 7.4):
 *   - Active (unresolved) incidents shown by default
 *   - Resolved incidents hidden behind a "+ Show resolved (N)" toggle
 *   - When count > 10 in the current period, the inline list collapses
 *     to a "View all N →" link to the standard Issues tab
 *   - Confidential incidents whose detail the viewer can't see render
 *     "🔒 Confidential incident" — severity and date still visible so
 *     the card count remains accurate even for unauthorized viewers
 *   - Bottom note explains the streak/lifecycle decoupling: closing an
 *     investigation does NOT reset the safety record
 *
 * Spec: docs/features/Life4Earth_HSE_Feature_Spec.md § 7.4
 * Mockup: mockups/hse_card_states.html (State 5)
 */

import React, { useMemo, useState } from 'react';
import { useAppTranslation, type TranslateFn } from '../../i18n/useAppTranslation';
import type { HseSubKpi } from '../../kpi-engine/hse';
import { severityIsRed, severityIsYellow } from '../../kpi-engine/hse';
import { useHseIncidents, useHseMiniProjectIncidents } from '../../api/hseIncidents';
import { periodStart } from '../../kpi-engine/missionTypes/incidentCount';
import type {
  HseIncident,
} from '../../kpi-engine/hse';
import type { HseCategory, HseSeverity } from '../../kpi-engine/missionTypes/types';
import styles from './IncidentDrillDown.module.css';

// ── Props ────────────────────────────────────────────────────────

export interface IncidentDrillDownProps {
  /** The HSE card whose drill-down this is. Provides category + period. */
  card: HseSubKpi;
  /** Project context for incident fetch. */
  projectGuid: string;
  /**
   * DEV-1141. When set, incidents are fetched from the mini-project HSE
   * route instead of the full-project one. FP behavior unchanged when omitted.
   */
  miniProjectGuid?: string;
  /** Called when the user clicks the X to close. */
  onClose: () => void;
  /**
   * Called when the user clicks "View all N →" in the >10 case. Parent
   * should navigate to the Risks & Issues tab pre-filtered to HSE.
   * If omitted, the link no-ops.
   */
  onViewAll?: (category: string) => void;
  /**
   * Called with the issue id when the user clicks an incident row.
   * Parent typically opens EditIncidentModal so the user can update
   * the investigation status without leaving the Mission tab. (DEV-822)
   */
  onIncidentClick?: (issueId: string) => void;
}

// ── Severity → resolved-status helpers ─────────────────────────────
// Severity colors are now category-aware; helpers come from kpi-engine/hse.

function isResolvedStatus(status: string | undefined): boolean {
  if (!status) return false;
  const s = status.toLowerCase();
  return s === 'closed' || s === 'mitigated' || s === 'resolved';
}

// ── Date formatting ─────────────────────────────────────────────

function formatIncidentDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso);
  if (Number.isNaN(d.getTime())) return iso;
  // "May 2, 9:15 AM" style
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ── Pagination threshold ────────────────────────────────────────

const PAGINATION_THRESHOLD = 10;

// ── Component ────────────────────────────────────────────────────

export function IncidentDrillDown({
  card,
  projectGuid,
  miniProjectGuid,
  onClose,
  onViewAll,
  onIncidentClick,
}: IncidentDrillDownProps) {
  const { t } = useAppTranslation('kpi');
  // Rules-of-hooks: call both unconditionally, but feed the inactive one an
  // empty guid (the hooks short-circuit to [] without a network call). The
  // owner kind never changes for a given mounted drill-down.
  const isMini = !!miniProjectGuid;
  const fpResult = useHseIncidents(isMini ? '' : projectGuid);
  const mpResult = useHseMiniProjectIncidents(isMini ? miniProjectGuid! : '');
  const { incidents, loading } = isMini ? mpResult : fpResult;
  /*
   * DEV-853: Default to showing resolved incidents so the drill-down list
   * count matches the card count. The HSE engine intentionally counts
   * closed incidents toward the period total (safety records are history,
   * not a to-do list — see the streakNote at the bottom of this panel).
   * Hiding closed incidents by default created a visible mismatch ("card
   * says 2, list shows 1") that confused users without changing the
   * actual semantics. The toggle is preserved for users who want to
   * focus on active investigations only.
   */
  const [showResolved, setShowResolved] = useState(true);

  const category = card.config.hse_category;
  const periodLabel = card.score.hse?.period_label_key
    ? t(card.score.hse.period_label_key.replace('kpi:', ''))
    : '';

  // Filter to this card's category + current period.
  const today = new Date().toISOString().slice(0, 10);
  const periodStartIso = periodStart(card.config.period, today);

  const inPeriod = useMemo(() => {
    return incidents
      .filter((i) => i.hse_category === category)
      .filter((i) => i.incident_date >= periodStartIso)
      .sort((a, b) => (a.incident_date < b.incident_date ? 1 : -1));
  }, [incidents, category, periodStartIso]);

  const active = useMemo(
    () => inPeriod.filter((i) => !isResolvedStatus(i.status)),
    [inPeriod],
  );
  const resolved = useMemo(
    () => inPeriod.filter((i) => isResolvedStatus(i.status)),
    [inPeriod],
  );

  // Determine the worst color for the panel's top stripe (category-aware).
  const topStripeColor: 'red' | 'yellow' | 'green' = useMemo(() => {
    if (inPeriod.some((i) => severityIsRed(i.severity, category))) return 'red';
    if (inPeriod.some((i) => severityIsYellow(i.severity, category))) return 'yellow';
    return 'green';
  }, [inPeriod, category]);

  // ── Render ────────────────────────────────────────────────────

  const totalInPeriod = inPeriod.length;
  const overThreshold = totalInPeriod > PAGINATION_THRESHOLD;

  return (
    <div
      className={styles.panel}
      data-color={topStripeColor}
      data-testid="incident-drill-down"
    >
      <div className={styles.head}>
        <h3 className={styles.title}>
          {t(`hse.category.${category}`)}{' '}
          {t('hse.drillDown.incidentsHeader')}
          {/* DEV-853: Show TOTAL incident count up front so the user can match
              it against the card. When some are resolved, supplement with the
              active breakdown so users still see how many are open. */}
          <span className={styles.titleMeta}>
            {' '}
            · {periodLabel} (
            {totalInPeriod}
            {resolved.length > 0
              ? ` · ${active.length} ${
                  active.length === 1
                    ? t('hse.drillDown.activeSingular')
                    : t('hse.drillDown.activePlural')
                }`
              : ''}
            )
          </span>
        </h3>
        <button
          className={styles.closeBtn}
          onClick={onClose}
          type="button"
          aria-label={t('hse.drillDown.close')}
        >
          ×
        </button>
      </div>

      {loading && (
        <div className={styles.loading}>{t('hse.drillDown.loading')}</div>
      )}

      {!loading && inPeriod.length === 0 && (
        <div className={styles.empty}>
          {t('hse.drillDown.empty')}
        </div>
      )}

      {!loading && overThreshold && (
        <div className={styles.overThreshold}>
          <div className={styles.overThresholdMessage}>
            {t('hse.drillDown.tooManyMessage', { count: totalInPeriod })}
          </div>
          <button
            className={styles.viewAllBtn}
            type="button"
            onClick={() => onViewAll?.(category)}
          >
            {t('hse.drillDown.viewAll', { count: totalInPeriod })} →
          </button>
        </div>
      )}

      {!loading && !overThreshold && active.length > 0 && (
        <div className={styles.list}>
          {active.map((incident) => (
            <IncidentRow
              key={incident.issue_id}
              incident={incident}
              category={category}
              onClick={onIncidentClick}
              t={t}
            />
          ))}
        </div>
      )}

      {!loading && !overThreshold && resolved.length > 0 && (
        <>
          <button
            className={styles.toggleResolved}
            type="button"
            onClick={() => setShowResolved((v) => !v)}
          >
            {showResolved
              ? t('hse.drillDown.hideResolved', { count: resolved.length })
              : t('hse.drillDown.showResolved', { count: resolved.length })}
          </button>
          {showResolved && (
            <div className={`${styles.list} ${styles.listResolved}`}>
              {resolved.map((incident) => (
                <IncidentRow
                  key={incident.issue_id}
                  incident={incident}
                  category={category}
                  onClick={onIncidentClick}
                  t={t}
                  resolved
                />
              ))}
            </div>
          )}
        </>
      )}

      {!loading && inPeriod.length > 0 && (
        <div className={styles.streakNote}>
          {t('hse.drillDown.streakNote')}
        </div>
      )}
    </div>
  );
}

// ── Sub-component: one incident row ─────────────────────────────

interface IncidentRowProps {
  incident: HseIncident;
  /** Card category — needed for severity color since the boundaries differ per scale. */
  category: HseCategory;
  /** Called when the user clicks the row. Parent opens the edit modal. */
  onClick?: (issueId: string) => void;
  resolved?: boolean;
  // DEV-963: accept the wrapper's full TranslateFn so positional-default
  // form `t(key, 'Default')` type-checks here too.
  t: TranslateFn;
}

function IncidentRow({ incident, category, onClick, resolved = false, t }: IncidentRowProps) {
  const severityClass =
    severityIsRed(incident.severity, category)
      ? styles.sevRed
      : severityIsYellow(incident.severity, category)
      ? styles.sevYellow
      : '';

  const statusKey = incident.status?.toLowerCase() ?? 'open';
  const statusLabel = t(`hse.drillDown.status.${statusKey}`, undefined);

  // Confidential title is already masked by the API (server-side
  // permission filter). UI just renders what came back.
  const isMaskedTitle =
    incident.confidential && (!incident.title || incident.title.startsWith('🔒'));

  const clickable = !!onClick && !!incident.issue_id;
  const handleClick = clickable
    ? () => onClick!(incident.issue_id)
    : undefined;
  const handleKeyDown = clickable
    ? (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick?.();
        }
      }
    : undefined;

  return (
    <div
      className={`${styles.row} ${resolved ? styles.rowResolved : ''} ${
        clickable ? styles.rowClickable : ''
      }`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={
        clickable
          ? t('hse.drillDown.editIncidentAria', 'Edit this incident')
          : undefined
      }
    >
      <span
        className={`${styles.severityPill} ${severityClass}`}
        data-severity={incident.severity}
      >
        {t(`hse.severity.${incident.severity}`)}
      </span>
      <div className={styles.titleCell}>
        {isMaskedTitle ? (
          <span className={styles.confidential}>
            🔒 {t('hse.drillDown.confidentialIncident')}
          </span>
        ) : (
          <>
            {incident.confidential && <span className={styles.lockIcon}>🔒 </span>}
            <span className={styles.titleText}>{incident.title ?? t('hse.drillDown.untitledIncident')}</span>
          </>
        )}
        {incident.issue_id && (
          <span className={styles.issueIdBadge}>
            {issueIdShort(incident.issue_id)}
          </span>
        )}
      </div>
      <span className={styles.dateCell}>
        {formatIncidentDate(incident.incident_date)}
      </span>
      <span
        className={styles.statusPill}
        data-status={statusKey}
      >
        {statusLabel || statusKey}
      </span>
      {/* DEV-850: Explicit edit-pencil glyph. The row is still clickable as a
          convenience target, but the pencil is the visible "this is editable"
          affordance that the status pill was unintentionally pretending to be.
          stopPropagation prevents double-firing the row's onClick. */}
      {clickable && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
          title={t('hse.drillDown.editIncidentTitle', 'Edit incident')}
          aria-label={t('hse.drillDown.editIncidentAria', 'Edit this incident')}
          style={{
            width: 24,
            height: 24,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 5,
            border: '1px solid var(--border-default)',
            background: 'transparent',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.13 1.87a1.25 1.25 0 0 1 1.77 0l1.23 1.23a1.25 1.25 0 0 1 0 1.77L5.04 13.96l-3.54.5.5-3.54L11.13 1.87Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9.5 3.5l3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
        </button>
      )}
    </div>
  );
}

/** Shorten a UUID to a readable badge ("abc12345"). */
function issueIdShort(id: string): string {
  if (!id) return '';
  // If it looks like a UUID, take the first 8 chars; otherwise return as-is
  // (e.g., "INC-0023" pre-formatted ids).
  if (/^[0-9a-f]{8}-/i.test(id)) return id.slice(0, 8);
  return id;
}

export default IncidentDrillDown;
